// The video compression decision rule (spec 2026-10-23-video-uploads-design.md "Compression").
// A port of Android's com.project.data.video.VideoDecision: same fields, same order, same guards,
// checked against the same case table (src/media/__tests__/video-decision-cases.json). Change both
// clients together; if a shared case fails, fix the code, never the case.

/**
 * A picked video as probed. `width`/`height` as stored (before `rotation`) on Android; the web passes
 * the display size with `rotation` 0 (the rule reads only the short edge); `durationMs` 0 = unknown;
 * `videoMime`/`audioMime` as MediaFormat MIME types (`audioMime` null = no audio track, "" = codec
 * unknown); `audioKbps` null = unknown.
 */
export interface VideoSource {
  bytes: number;
  durationMs: number;
  width: number;
  height: number;
  rotation: number;
  videoMime: string;
  audioMime: string | null;
  audioKbps: number | null;
  hdr: boolean;
}

/**
 * ORIGINAL: send the source (`remux`: rewrite it into an MP4 with moov first, no re-encode;
 * `cantPlayHint`: not H.264/AAC, players without that decoder show "Can't play here – download").
 * COMPRESS: re-encode to H.264/AAC MP4 with the short edge `shortEdge`, video `videoBps`, at most `fps`.
 * REJECTED: bigger than 1 GB and cannot be compressed under it.
 */
export type VideoPlan =
  | { plan: "ORIGINAL"; remux: boolean; cantPlayHint: boolean }
  | { plan: "COMPRESS"; shortEdge: number; videoBps: number; fps: number }
  | { plan: "REJECTED"; reason: "TOO_LARGE" };

export const MIME_H264 = "video/avc";
export const MIME_AAC = "audio/mp4a-latm";

/** The rule's constants (Android VideoRule + VideoTarget). */
export const VideoRule = {
  TARGET_MBPS: 2.5,
  AUDIO_MBPS: 0.128,
  SLACK: 1.3,
  MIN_SAVING_S: 5,
  MIN_SAVING_FRACTION: 0.15,
  DEFAULT_UPLINK_MBPS: 8,
  REESTIMATE_AFTER_MS: 3_000,
  OVERSHOOT: 1.5,
  /** file-stc `MaxVideo`. */
  MAX_BYTES: 2 ** 30,
  SHORT_EDGE: 720,
  VIDEO_BPS: 2_500_000,
  FPS: 30,
  GOP_SEC: 2,
  AUDIO_BPS: 128_000,
  AUDIO_SAMPLE_RATE: 48_000,
  /** AAC up to this bitrate passes through untouched. */
  AUDIO_PASSTHROUGH_MAX_KBPS: 160,
} as const;

/** The web's default encoder speed with hardware WebCodecs (× real time); 0 without it. */
export const DEFAULT_SPEED_WEB_HW = 4;

export function shortEdgeOf(src: VideoSource): number {
  return src.width > 0 && src.height > 0 ? Math.min(src.width, src.height) : 0;
}

/** Plays everywhere as it is: H.264 video, AAC audio (or none). */
export function isH264Aac(src: VideoSource): boolean {
  return src.videoMime === MIME_H264 && (src.audioMime == null || src.audioMime === MIME_AAC);
}

/** A codec is known to be something else than H.264/AAC. An empty MIME = unknown: no hint. */
export function cantPlay(src: VideoSource): boolean {
  return !isH264Aac(src) && src.videoMime !== "" && src.audioMime !== "";
}

/** Source bitrate in Mbit/s (all tracks), or null when the duration is unknown. */
export function mbpsOf(src: VideoSource): number | null {
  return src.durationMs > 0 ? (src.bytes * 8) / (src.durationMs / 1000) / 1e6 : null;
}

/** The compressed size the target bitrates give for `durationMs`. */
export function expectedBytes(durationMs: number): number {
  return Math.trunc(((durationMs / 1000) * (VideoRule.TARGET_MBPS + VideoRule.AUDIO_MBPS) * 1e6) / 8);
}

export interface DecideOptions {
  /** null = not measured yet (default 8 Mbit/s). */
  uplinkMbps: number | null;
  /** null = not measured yet (`defaultSpeedX`). */
  speedX: number | null;
  canEncode: boolean;
  canToneMap: boolean;
  /** The platform default speed: 4 with hardware WebCodecs, 0 without. */
  defaultSpeedX: number;
}

/**
 * Order: no encoder → original; HDR that cannot be tone-mapped → original (before the codec check:
 * an HEVC HDR clip cannot become H.264 SDR without tone-mapping); not H.264/AAC → compress; already
 * small → original (remuxed); else compress when it saves enough upload time. Then the 1 GB guard:
 * an original over 1 GB is compressed when it can be and the result fits, else refused.
 */
export function decide(src: VideoSource, opts: DecideOptions): VideoPlan {
  const u = opts.uplinkMbps != null && opts.uplinkMbps > 0 ? opts.uplinkMbps : VideoRule.DEFAULT_UPLINK_MBPS;
  const r = opts.speedX ?? opts.defaultSpeedX;
  const compressible = opts.canEncode && r > 0 && !(src.hdr && !opts.canToneMap);
  const edge = shortEdgeOf(src);
  const compress: VideoPlan = {
    plan: "COMPRESS",
    shortEdge: edge > 0 ? Math.min(edge, VideoRule.SHORT_EDGE) : VideoRule.SHORT_EDGE,
    videoBps: VideoRule.VIDEO_BPS,
    fps: VideoRule.FPS,
  };
  const original = (remux: boolean): VideoPlan => ({ plan: "ORIGINAL", remux, cantPlayHint: cantPlay(src) });

  const bSrc = mbpsOf(src);
  let plan: VideoPlan;
  if (!compressible) plan = original(false);
  else if (!isH264Aac(src)) plan = compress;
  else if (bSrc == null) plan = original(false);
  else if (edge <= VideoRule.SHORT_EDGE && bSrc <= VideoRule.SLACK * (VideoRule.TARGET_MBPS + VideoRule.AUDIO_MBPS))
    plan = original(true);
  else {
    const durationS = src.durationMs / 1000;
    const saving = (durationS * (bSrc - VideoRule.TARGET_MBPS)) / u;
    const cost = durationS / r;
    plan =
      saving - cost < Math.max(VideoRule.MIN_SAVING_S, VideoRule.MIN_SAVING_FRACTION * saving)
        ? original(false)
        : compress;
  }

  const estimate = src.durationMs > 0 ? expectedBytes(src.durationMs) : null;
  if (plan.plan === "ORIGINAL" && src.bytes > VideoRule.MAX_BYTES) {
    return compressible && estimate != null && estimate <= VideoRule.MAX_BYTES
      ? compress
      : { plan: "REJECTED", reason: "TOO_LARGE" };
  }
  if (plan.plan === "COMPRESS" && estimate != null && estimate > VideoRule.MAX_BYTES) {
    return src.bytes <= VideoRule.MAX_BYTES ? original(false) : { plan: "REJECTED", reason: "TOO_LARGE" };
  }
  return plan;
}

/**
 * After REESTIMATE_AFTER_MS of encoding: false when finishing the encode and uploading the result now
 * projects slower than uploading the original (cancel, send the original). Only for sources that may
 * go as they are.
 */
export function keepCompressing(
  src: VideoSource,
  elapsedMs: number,
  progress: number,
  uplinkMbps: number | null,
): boolean {
  if (elapsedMs < VideoRule.REESTIMATE_AFTER_MS || !isH264Aac(src) || src.bytes > VideoRule.MAX_BYTES || src.durationMs <= 0)
    return true;
  if (progress <= 0) return true;
  const u = uplinkMbps != null && uplinkMbps > 0 ? uplinkMbps : VideoRule.DEFAULT_UPLINK_MBPS;
  const elapsedS = elapsedMs / 1000;
  const remainingEncodeS = (elapsedS * (1 - progress)) / progress;
  const compressedUploadS = (expectedBytes(src.durationMs) * 8) / 1e6 / u;
  const originalUploadS = (src.bytes * 8) / 1e6 / u;
  return remainingEncodeS + compressedUploadS <= originalUploadS;
}

export type EncodeOutcome = "KEEP" | "USE_ORIGINAL" | "TOO_LARGE";

/**
 * The encode finished with `outputBytes`: keep it, send the original instead (overshoot guard), or
 * give up. A source that is not H.264/AAC keeps its playable H.264 output whenever it fits in 1 GB
 * (ruling 2026-10-23).
 */
export function afterEncode(src: VideoSource, outputBytes: number): EncodeOutcome {
  const overshoot = src.durationMs > 0 && outputBytes > expectedBytes(src.durationMs) * VideoRule.OVERSHOOT;
  const worse = overshoot || outputBytes >= src.bytes;
  if (src.bytes > VideoRule.MAX_BYTES || !isH264Aac(src)) {
    if (outputBytes <= VideoRule.MAX_BYTES) return "KEEP";
    return src.bytes <= VideoRule.MAX_BYTES ? "USE_ORIGINAL" : "TOO_LARGE";
  }
  return worse ? "USE_ORIGINAL" : "KEEP";
}

/** The encode failed (or cannot run): the original goes if it may, else the send is refused. */
export function afterFailure(src: VideoSource): VideoPlan {
  return src.bytes <= VideoRule.MAX_BYTES
    ? { plan: "ORIGINAL", remux: false, cantPlayHint: cantPlay(src) }
    : { plan: "REJECTED", reason: "TOO_LARGE" };
}
